import React, {CSSProperties, FunctionComponent} from "react";
import {Box, makeStyles} from "@material-ui/core";
import {removeDisposal, loadDisposalToCurrent, EnhancedDisposal} from "../../../state/disposalSlice";
import UnderlinedTextButton from "../../../../commonComponents/UnderlinedTextButton";
import Text from "../../../../commonComponents/Text";
import {useDispatch} from "react-redux";
import schmuck from "../../../../images/ObjectTypes/button-schmuck.svg";
import uhr from "../../../../images/ObjectTypes/button-uhr.svg";
import muenzenBarren from "../../../../images/ObjectTypes/button-muenzen.svg";
import zahngold from "../../../../images/ObjectTypes/button-zahngold.svg";
import diamanten from "../../../../images/ObjectTypes/button-diamanten.svg";
import {I18n} from "aws-amplify";
import {Paper} from "@material-ui/core";
import {Step} from "../../../WizardMap";
import {DisposalType} from "../../../../API";

type DisposalSummaryProps = {
    disposal: EnhancedDisposal;
    style?: CSSProperties,
    jumpTo: (step: Step) => void;
};

const useStyles = makeStyles(() => ({
    disposalItemContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderRadius: 2,
        padding: 10,
        width: 250,
        minHeight: 107
    },
    contentContainer: {
        display: "flex"
    },
    textBlock: {
        display: "flex",
        flexDirection: "column",
        marginLeft: 10
    },
    materialContainer: {
        width: 80
    }
}));

const fontSize = 12.7;

const disposalTypeToImage = (type: DisposalType | null): string | undefined => {
    switch (type) {
        case DisposalType.JEWELLERY:
            return schmuck;
        case DisposalType.COIN_BAR:
            return muenzenBarren;
        case DisposalType.WATCH:
            return uhr;
        case DisposalType.DENTAL_GOLD:
            return zahngold;
        case DisposalType.DIAMOND:
            return diamanten;
    }
};

const disposalTypeToStep = (type: DisposalType | null): Step | undefined => {
    switch (type) {
        case DisposalType.JEWELLERY:
            return Step.JEWELLERY_MATERIAL;
        case DisposalType.COIN_BAR:
            return Step.COIN_BAR_MATERIAL;
        case DisposalType.WATCH:
            return Step.WATCH_BRAND;
        case DisposalType.DIAMOND:
            return Step.ADD_INFO;
        case DisposalType.DENTAL_GOLD:
            return Step.ADD_INFO;
    }
};

const DisposalSummaryItem: FunctionComponent<DisposalSummaryProps> = ({disposal, jumpTo}) => {

    const classes = useStyles();

    const dispatch = useDispatch();

    const {image, materials, brand, referenceNumber, model} = disposal;

    const imageUrl: string | undefined = image ? image.url : disposalTypeToImage(disposal.type as DisposalType);

    const handleEditClicked = () => {
        jumpTo(disposalTypeToStep(disposal.type as DisposalType) as Step);
        dispatch(loadDisposalToCurrent(disposal));
    };

    return (
        <Box padding={1}>
            <Paper className={classes.disposalItemContainer}>
                <Box className={classes.contentContainer}>
                    <Box width={78}>
                        <img src={imageUrl} alt={"thumbnail"} style={{width: 78}}/>
                    </Box>
                    <Box className={classes.contentContainer}>
                        <Box className={classes.textBlock}>
                            <Text value={I18n.get("common").product.concat(":")} size={fontSize}/>
                            {materials &&
                            <Text value={I18n.get("common").material.concat(":")} size={fontSize}/>}
                            {brand &&
                            <Text value={I18n.get("common").brand.concat(":")} size={fontSize}/>}
                            {model &&
                            <Text value={I18n.get("common").model.concat(":")} size={fontSize}/>}
                            {referenceNumber &&
                            <Text value={I18n.get("common").refNr.concat(":")} size={fontSize}/>}
                        </Box>
                        <Box className={classes.textBlock}>
                            <Text
                                value={disposal.type ? I18n.get("disposalType")[disposal.type] : ""}
                                size={fontSize}
                            />
                            {materials &&
                            <Box className={classes.materialContainer}>
                                <Text
                                    value={materials.map(item => I18n.get("material")[item]).join(", ")}
                                    size={fontSize}/>
                            </Box>}
                            {disposal.brand &&
                            <Text
                                value={disposal.brand.value ? disposal.brand.value : I18n.get("common").unknown}
                                size={fontSize}/>}
                            {disposal.model &&
                            <Text
                                value={disposal.model.value ? disposal.model.value : I18n.get("common").unknown}
                                size={fontSize}/>}
                            {disposal.referenceNumber &&
                            <Text
                                value={disposal.referenceNumber.value ? disposal.referenceNumber.value : I18n.get("common").unknown}
                                size={fontSize}/>}
                        </Box>
                    </Box>
                </Box>
                <Box display="flex" justifyContent={"space-between"}>
                    <UnderlinedTextButton
                        text={I18n.get("common").edit} onClick={handleEditClicked}
                        fontSize={13}/>
                    <UnderlinedTextButton
                        text={I18n.get("common").delete} onClick={() => dispatch(removeDisposal(disposal))}
                        fontSize={13}/>
                </Box>
            </Paper>
        </Box>
    );
};

export default DisposalSummaryItem;
